import { IContact } from "../../../types/contact";

const escapeVCardValue = (value: string): string =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

const valueLine = (
  field: string,
  value: string | null | undefined,
): string | null =>
  value?.trim() ? `${field}:${escapeVCardValue(value.trim())}` : null;

const contactName = (contact: IContact): string =>
  contact.contact_type === "company"
    ? contact.name || contact.company_name || "Unnamed Company"
    : `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
      contact.name ||
      "Unnamed Contact";

export const contactToVCard = (contact: IContact): string => {
  const isCompany = contact.contact_type === "company";
  const fullName = contactName(contact);
  const firstName = isCompany ? "" : contact.first_name || "";
  const lastName = isCompany ? "" : contact.last_name || "";
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${escapeVCardValue(fullName)}`,
    `N:${escapeVCardValue(lastName)};${escapeVCardValue(firstName)};;;`,
    valueLine("ORG", isCompany ? fullName : contact.company_name),
    // Include a company-industry hint and company-email as custom fields
    valueLine("X-INDUSTRY", contact.industry),
    valueLine("X-COMPANY-EMAIL", contact.company_email),
    // If an image URL is present, reference it in PHOTO as a URI (clients may fetch it)
    valueLine("PHOTO;VALUE=URI", contact.contact_image),
    valueLine("TITLE", contact.jobTitle),
    valueLine("EMAIL;TYPE=INTERNET", contact.email),
    valueLine("TEL;TYPE=CELL", contact.phone),
    valueLine("URL", contact.website),
    // Work address (single-line fallback)
    valueLine("ADR;TYPE=WORK", contact.address),
    valueLine("NOTE", contact.notes),
    "END:VCARD",
  ];

  return lines.filter((line): line is string => line !== null).join("\r\n");
};

export const contactsToVCard = (contacts: IContact[]): string =>
  contacts.map(contactToVCard).join("\r\n");

export const downloadVCard = (contacts: IContact[], filename: string): void => {
  const content = contactsToVCard(contacts);
  const blob = new Blob([content], { type: "text/vcard;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const vCardFilename = (contact: IContact): string =>
  `${
    contactName(contact)
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "") || "contact"
  }.vcf`;
